export const PERMISSIONS = {
  // Menus
  MENU_DASHBOARD:      'MENU_DASHBOARD',
  MENU_ATTENDANCE:     'MENU_ATTENDANCE',
  MENU_LEAVE:          'MENU_LEAVE',
  MENU_HOLIDAY:        'MENU_HOLIDAY',
  MENU_SHIFTS:         'MENU_SHIFTS',
  MENU_DEPARTMENTS:    'MENU_DEPARTMENTS',
  MENU_USERS:          'MENU_USERS',
  MENU_ACCOUNTING:     'MENU_ACCOUNTING',
  MENU_PAYROLL:        'MENU_PAYROLL',
  MENU_TAX:            'MENU_TAX',
  MENU_DEVICES:        'MENU_DEVICES',
  MENU_API_KEYS:       'MENU_API_KEYS',
  MENU_NOTICES:        'MENU_NOTICES',
  MENU_TASKS:          'MENU_TASKS',
  MENU_APPRAISAL:      'MENU_APPRAISAL',
  // Shift sub-menus
  SUBMENU_SHIFT_LIST:            'SUBMENU_SHIFT_LIST',
  SUBMENU_SHIFT_ASSIGNMENTS:     'SUBMENU_SHIFT_ASSIGNMENTS',
  SUBMENU_SHIFT_ROTATION_PATTERNS: 'SUBMENU_SHIFT_ROTATION_PATTERNS',
  SUBMENU_SHIFT_ROSTER:          'SUBMENU_SHIFT_ROSTER',
  SUBMENU_SHIFT_ORPHANS:         'SUBMENU_SHIFT_ORPHANS',
  // Leave sub-menus
  SUBMENU_LEAVE_ALL_APPLICATIONS: 'SUBMENU_LEAVE_ALL_APPLICATIONS',
  SUBMENU_LEAVE_BALANCES:         'SUBMENU_LEAVE_BALANCES',
  SUBMENU_LEAVE_TYPES:            'SUBMENU_LEAVE_TYPES',
  SUBMENU_LEAVE_POLICY:           'SUBMENU_LEAVE_POLICY',
  SUBMENU_LEAVE_ALLOCATIONS:      'SUBMENU_LEAVE_ALLOCATIONS',
  // Actions
  ACTION_APPROVE_LEAVE:          'ACTION_APPROVE_LEAVE',
  ACTION_CREATE_USER:            'ACTION_CREATE_USER',
  ACTION_CREATE_JOURNAL_ENTRY:   'ACTION_CREATE_JOURNAL_ENTRY',
  ACTION_MANAGE_PAYROLL:         'ACTION_MANAGE_PAYROLL',
  ACTION_MANAGE_HOLIDAYS:        'ACTION_MANAGE_HOLIDAYS',
  ACTION_MANAGE_DEVICES:         'ACTION_MANAGE_DEVICES',
  ACTION_MANAGE_NOTICES:         'ACTION_MANAGE_NOTICES',
  ACTION_ASSIGN_TASK:            'ACTION_ASSIGN_TASK',
  // Dashboard views — which dashboard board(s) a user may see. Default follows role
  // (owner=InstitutionAdmin, manager=InstitutionManager, accountant=Accountant); an admin
  // can override per-user here without creating a dedicated role.
  DASHBOARD_VIEW_OWNER:          'DASHBOARD_VIEW_OWNER',
  DASHBOARD_VIEW_MANAGER:        'DASHBOARD_VIEW_MANAGER',
  DASHBOARD_VIEW_ACCOUNTANT:     'DASHBOARD_VIEW_ACCOUNTANT',
} as const;

export type PermissionKey = typeof PERMISSIONS[keyof typeof PERMISSIONS];

// `label` / `group` are the English display text. The translated text lives in the `users` namespace:
// `permissionCatalog.labels.<key>` for each permission and `permissionCatalog.groups.<groupKey>` for groups.
type CatalogSubItem = { key: PermissionKey; label: string };
type CatalogItem = { key: PermissionKey; label: string; subItems?: CatalogSubItem[] };
export type PermissionGroupKey = 'menus' | 'actions' | 'dashboardViews';
type CatalogGroup = { group: string; groupKey: PermissionGroupKey; items: CatalogItem[] };

export const PERMISSION_CATALOG: CatalogGroup[] = [
  {
    group: 'Menus',
    groupKey: 'menus',
    items: [
      { key: PERMISSIONS.MENU_DASHBOARD,    label: 'Dashboard' },
      { key: PERMISSIONS.MENU_ATTENDANCE,   label: 'Attendance' },
      { key: PERMISSIONS.MENU_LEAVE, label: 'Leave', subItems: [
        { key: PERMISSIONS.SUBMENU_LEAVE_ALL_APPLICATIONS, label: 'All Applications' },
        { key: PERMISSIONS.SUBMENU_LEAVE_BALANCES,         label: 'Leave Balances' },
        { key: PERMISSIONS.SUBMENU_LEAVE_TYPES,            label: 'Leave Types' },
        { key: PERMISSIONS.SUBMENU_LEAVE_POLICY,           label: 'Leave Policy' },
        { key: PERMISSIONS.SUBMENU_LEAVE_ALLOCATIONS,      label: 'Leave Allocations' },
      ]},
      { key: PERMISSIONS.MENU_HOLIDAY,      label: 'Holidays' },
      { key: PERMISSIONS.MENU_SHIFTS, label: 'Shifts', subItems: [
        { key: PERMISSIONS.SUBMENU_SHIFT_LIST,            label: 'Shift List' },
        { key: PERMISSIONS.SUBMENU_SHIFT_ASSIGNMENTS,     label: 'Assignments' },
        { key: PERMISSIONS.SUBMENU_SHIFT_ROTATION_PATTERNS, label: 'Rotation Patterns' },
        { key: PERMISSIONS.SUBMENU_SHIFT_ROSTER,          label: 'Roster' },
        { key: PERMISSIONS.SUBMENU_SHIFT_ORPHANS,         label: 'Orphan Punches' },
      ]},
      { key: PERMISSIONS.MENU_DEPARTMENTS, label: 'Departments' },
      { key: PERMISSIONS.MENU_USERS,        label: 'Users' },
      { key: PERMISSIONS.MENU_ACCOUNTING,   label: 'Accounting' },
      { key: PERMISSIONS.MENU_PAYROLL,      label: 'Payroll' },
      { key: PERMISSIONS.MENU_DEVICES,      label: 'Devices' },
      { key: PERMISSIONS.MENU_API_KEYS,     label: 'API Keys' },
      { key: PERMISSIONS.MENU_NOTICES,      label: 'Notices' },
      { key: PERMISSIONS.MENU_TASKS,        label: 'Tasks' },
      { key: PERMISSIONS.MENU_APPRAISAL,    label: 'Appraisal cycles' },
    ],
  },
  {
    group: 'Actions',
    groupKey: 'actions',
    items: [
      { key: PERMISSIONS.ACTION_APPROVE_LEAVE,        label: 'Approve / Reject Leave' },
      { key: PERMISSIONS.ACTION_CREATE_USER,          label: 'Create Users' },
      { key: PERMISSIONS.ACTION_CREATE_JOURNAL_ENTRY, label: 'Create Journal Entries' },
      { key: PERMISSIONS.ACTION_MANAGE_PAYROLL,       label: 'Manage Payroll' },
      { key: PERMISSIONS.ACTION_MANAGE_HOLIDAYS,      label: 'Manage Holidays' },
      { key: PERMISSIONS.ACTION_MANAGE_DEVICES,       label: 'Manage Devices' },
      { key: PERMISSIONS.ACTION_MANAGE_NOTICES,       label: 'Manage Notices' },
      { key: PERMISSIONS.ACTION_ASSIGN_TASK,          label: 'Assign Tasks' },
    ],
  },
  {
    group: 'Dashboard views',
    groupKey: 'dashboardViews',
    items: [
      { key: PERMISSIONS.DASHBOARD_VIEW_OWNER,      label: 'Business-owner dashboard' },
      { key: PERMISSIONS.DASHBOARD_VIEW_MANAGER,    label: 'Manager dashboard' },
      { key: PERMISSIONS.DASHBOARD_VIEW_ACCOUNTANT, label: 'Accountant dashboard' },
    ],
  },
];
